import React, { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  PersonAdd as SignupIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const LoginPage: React.FC = () => {
  usePageTitle('Login');
  const [tabValue, setTabValue] = useState(0);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'resident' as 'resident' | 'supervisor' | 'admin',
    specialties: [] as string[],
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, signup, loginAsDemo } = useAuth();
  const navigate = useNavigate();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(loginData.email, loginData.password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signupData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signup({
        name: signupData.name,
        email: signupData.email,
        password: signupData.password,
        role: signupData.role,
        specialties: signupData.specialties,
      });
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (role: 'resident' | 'supervisor' | 'admin') => {
    setError('');
    setLoading(true);
    
    try {
      await loginAsDemo(role);
      navigate('/');
    } catch (err) {
      setError('Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  const availableSpecialties = [
    'Radiology',
    'Nuclear Medicine',
    'Interventional Radiology',
    'Neuroradiology',
    'Musculoskeletal Imaging',
    'Cardiothoracic Imaging',
    'Abdominal Imaging',
    'Pediatric Radiology',
    'Emergency Radiology',
    'Administration',
  ];

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <AdminIcon />;
      case 'supervisor': return <SupervisorIcon />;
      default: return <PersonIcon />;
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Full system access, user management, workflow configuration';
      case 'supervisor':
        return 'Review resident work, approve reports, manage assignments';
      case 'resident':
        return 'Create reports, request consultations, view assigned clinicalItems';
      default:
        return '';
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 3, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            OS Workflow Platform
          </Typography>
          <Typography variant="subtitle1">
            Radiology Workflow Management System
          </Typography>
        </Box>

        {/* Demo Users Section */}
        <Box sx={{ p: 3, bgcolor: 'background.default' }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2, color: 'text.primary' }}>
            🚀 Quick Demo Access
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" gap={2}>
            <Card sx={{ minWidth: 200, flex: 1, maxWidth: 250 }}>
              <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                <PersonIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6">Resident</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Dr. Jane Resident
                </Typography>
                <Typography variant="caption" display="block">
                  Create reports, view studies
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleDemoLogin('resident')}
                  disabled={loading}
                >
                  Demo Login
                </Button>
              </CardActions>
            </Card>

            <Card sx={{ minWidth: 200, flex: 1, maxWidth: 250 }}>
              <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                <SupervisorIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6">Supervisor</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Dr. John Supervisor
                </Typography>
                <Typography variant="caption" display="block">
                  Review, approve reports
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  onClick={() => handleDemoLogin('supervisor')}
                  disabled={loading}
                >
                  Demo Login
                </Button>
              </CardActions>
            </Card>

            <Card sx={{ minWidth: 200, flex: 1, maxWidth: 250 }}>
              <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                <AdminIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6">Admin</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Dr. Sarah Admin
                </Typography>
                <Typography variant="caption" display="block">
                  Full system access
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDemoLogin('admin')}
                  disabled={loading}
                >
                  Demo Login
                </Button>
              </CardActions>
            </Card>
          </Stack>
        </Box>

        <Divider />

        {/* Authentication Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab
              label="Login"
              icon={<LoginIcon />}
              iconPosition="start"
              id="auth-tab-0"
              aria-controls="auth-tabpanel-0"
            />
            <Tab
              label="Sign Up"
              icon={<SignupIcon />}
              iconPosition="start"
              id="auth-tab-1"
              aria-controls="auth-tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ m: 3, mb: 0 }}>
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <TabPanel value={tabValue} index={0}>
          <form onSubmit={handleLogin}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                placeholder="your.email@example.com"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                placeholder="Enter your password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
              
              <Typography variant="body2" color="text.secondary" textAlign="center">
                💡 Tip: Use demo credentials above or any email/password for testing
              </Typography>
            </Stack>
          </form>
        </TabPanel>

        {/* Signup Form */}
        <TabPanel value={tabValue} index={1}>
          <form onSubmit={handleSignup}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Full Name"
                value={signupData.name}
                onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                required
                placeholder="Dr. John Smith"
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={signupData.email}
                onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                required
                placeholder="john.smith@hospital.com"
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  required
                  placeholder="Minimum 6 characters"
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                  required
                  placeholder="Confirm your password"
                />
              </Stack>
              
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={signupData.role}
                  label="Role"
                  onChange={(e) => setSignupData({ ...signupData, role: e.target.value as any })}
                >
                  <MenuItem value="resident">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon fontSize="small" />
                      <Box>
                        <Typography variant="body1">Resident</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getRoleDescription('resident')}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="supervisor">
                    <Box display="flex" alignItems="center" gap={1}>
                      <SupervisorIcon fontSize="small" />
                      <Box>
                        <Typography variant="body1">Supervisor</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getRoleDescription('supervisor')}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AdminIcon fontSize="small" />
                      <Box>
                        <Typography variant="body1">Administrator</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getRoleDescription('admin')}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Specialties</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(signupData.specialties) ? signupData.specialties : []}
                  label="Specialties"
                  onChange={(e) => setSignupData({ ...signupData, specialties: Array.isArray(e.target.value) ? e.target.value : [] })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {availableSpecialties.map((specialty) => (
                    <MenuItem key={specialty} value={specialty}>
                      {specialty}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>
            </Stack>
          </form>
        </TabPanel>

        {/* Footer */}
        <Box sx={{ p: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            This is a proof-of-concept application for demonstration purposes
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;